import {
  faArrowDown as ArrowDownIcon,
  faArrowUp as ArrowUpIcon
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Chip, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useAtom } from 'jotai';
import { sortBy } from 'lodash';
import { useMemo } from 'react';

import DonutChart from '@/components/charts/DonutChart/DonutChart';
import HorizontalBarChart from '@/components/charts/HorizontalBarChart';
import { STATUS_COLORS } from '@/constants/chartcolors.constants';
import { YEAR_MONTH_DAY_FORMAT } from '@/constants/date.constants';
import REPORT_PARAGRAPHS from '@/constants/reportparagraph.constants';
import AvgAckTimeWidget from '@/features/AvgAckTimeWidget';
import ReportsTemplateBoxContainer from '@/features/ReportsTemplateBoxContainer/ReportsTemplateBoxContainer';
import ReportsTemplateCompareWidget from '@/features/ReportsTemplateCompareWidget/ReportsTemplateCompareWidget';
import ReportFullWidthContainer from '@/features/ReportsTemplateFullWidthContainer/ReportsFullWidthContainer';
import ReportParagraph from '@/features/ReportsTemplateParagraph/ReportsParagraph';
import ReportsTemplateSectionTitle from '@/features/ReportsTemplateSectionTitle/ReportsTemplateSectionTitle';
import type { TicketsAckTimesDTO } from '@/pages/Dashboard/Dashboard.types';
import { calcChangePercentage } from '@/pages/Dashboard/Dashboard.utils';
import type { TicketMetricsByPriority } from '@/pages/ReportsTemplates/ReportsTemplate.types';
import { REPORTS_EXEC_TICKETS_HORIZONTAL_BAR_SERIES } from '@/pages/ReportsTemplates/ReportsTemplates.constants';
import type { Ticket } from '@/pages/Tickets';
import { useFetchV2Tickets } from '@/pages/Tickets/Tickets.store';
import { formatTickets } from '@/pages/Tickets/Tickets.utils';

import {
  reportsEndDateAtom,
  reportsFrequencyAtom,
  reportsOrgLabelAtom,
  reportsStartDateAtom,
  reportsTimeFrameAtom
} from '../../ReportsTemplate.hooks';
import useStyles from '../ReportsPages.styles';
import { getMetricTimes } from '../TicketsPages/TicketsPages.utils';

const ExecutiveReportPage2 = ({
  newTickets,
  orgAllMetrics
}: {
  newTickets: Ticket[];
  orgAllMetrics: TicketMetricsByPriority[];
}) => {
  const { classes } = useStyles();
  const [endDate] = useAtom(reportsEndDateAtom);
  const [startDate] = useAtom(reportsStartDateAtom);
  const [reportsFrequency] = useAtom(reportsFrequencyAtom);
  const [selectedOrganization] = useAtom(reportsOrgLabelAtom);
  const [timeFrame] = useAtom(reportsTimeFrameAtom);

  const prevSearchParams = {
    published_at__gte: dayjs(startDate)
      .subtract(Number(timeFrame), 'days')
      .format(YEAR_MONTH_DAY_FORMAT),
    published_at__lte: dayjs(endDate)
      .subtract(Number(timeFrame), 'days')
      .format(YEAR_MONTH_DAY_FORMAT),
    organization__label: selectedOrganization,
    ticket_type__in: ['1'] // Filter for THREAT tickets only
  };

  const { data: previousNewTickets } = useFetchV2Tickets({
    enabled: true,
    searchParams: prevSearchParams,
    fetchAllPages: true
  });

  const threatTicketsInTimeFrame = useMemo(
    () => newTickets.filter((ticket) => ticket.ticket_type === 1),
    [newTickets]
  );

  const threatTicketsInPreviousTimeFrame =
    previousNewTickets?.filter((ticket: Ticket) => ticket.ticket_type === 1) ||
    [];

  const resolvedThreatTickets = threatTicketsInTimeFrame.filter(
    (ticket: Ticket) => ticket?.ticket_status?.id === 3
  );

  const previousResolvedThreatTickets = threatTicketsInPreviousTimeFrame.filter(
    (ticket: Ticket) => ticket?.ticket_status?.id === 3
  );

  const unresolvedAfter7Days = threatTicketsInTimeFrame.filter(
    (ticket: Ticket) =>
      ticket?.ticket_status?.id !== 3 &&
      Math.abs(dayjs(ticket.published_at).diff(endDate, 'day')) > 7
  );

  const prevEndDate = dayjs(startDate).subtract(1, 'day');

  const previousUnresolvedAfter7Days = threatTicketsInPreviousTimeFrame.filter(
    (ticket: Ticket) =>
      ticket?.ticket_status?.id !== 3 &&
      Math.abs(dayjs(ticket?.published_at).diff(prevEndDate, 'day')) > 7
  );

  const bannerValues = [
    {
      title: 'New',
      total: threatTicketsInTimeFrame.length,
      diff: calcChangePercentage(
        threatTicketsInTimeFrame.length || 0,
        threatTicketsInPreviousTimeFrame.length || 0
      )
        .toFixed(2)
        .toString()
    },
    {
      title: 'Resolved',
      total: resolvedThreatTickets.length,
      diff: calcChangePercentage(
        resolvedThreatTickets.length || 0,
        previousResolvedThreatTickets.length || 0
      )
        .toFixed(2)
        .toString()
    },
    {
      title: 'Unresolved After 7 Days',
      total: unresolvedAfter7Days.length,
      diff: calcChangePercentage(
        unresolvedAfter7Days.length || 0,
        previousUnresolvedAfter7Days.length || 0
      )
        .toFixed(2)
        .toString()
    }
  ];

  const newTicketsSeverityData = useMemo(() => {
    const ticketsInTimeFrame = threatTicketsInTimeFrame.filter(
      (ticket: Ticket) => ticket.ticket_type === 1
    );
    const results = formatTickets(
      ticketsInTimeFrame,
      'ticket_priority.name' as 'ticket_priority'
    );

    const statusOrder = Object.keys(STATUS_COLORS);
    return sortBy(results, (result) => statusOrder.indexOf(result.name));
  }, [threatTicketsInTimeFrame]);

  const newTicketsCategoryData = useMemo(() => {
    const ticketsInTimeFrame = threatTicketsInTimeFrame.filter(
      (ticket: Ticket) => ticket.ticket_type === 1
    );
    const results = formatTickets(
      ticketsInTimeFrame,
      'ticket_category.name' as 'ticket_category'
    );

    const coloredResults = results.slice(0, 5).map((result) => {
      return {
        ...result,
        color: '#075B64'
      };
    });

    return coloredResults;
  }, [threatTicketsInTimeFrame]);

  const mttdMetrics = getMetricTimes('mttd', orgAllMetrics);
  const mttcMetrics = getMetricTimes('mttc', orgAllMetrics);
  const mttnMetrics = getMetricTimes('mttn', orgAllMetrics);

  return (
    <div className={classes.reportsPageContainer}>
      <ReportsTemplateSectionTitle>
        KEY THREAT & OPERATIONS METRICS
      </ReportsTemplateSectionTitle>
      <ReportParagraph>
        {`The Executive Summary provides a high-level snapshot of ${reportsFrequency.toLowerCase()} threat activity and ticket handling trends. It highlights key metrics such as the volume of new and resolved tickets, as well as outstanding tickets that remain open for more than seven days—offering insight into workload, resolution efficiency, and potential backlog risks. These indicators help security and operational stakeholders quickly assess changes in activity levels, understand the nature and urgency of emerging threats, and identify areas that may warrant deeper investigation or process refinement.`}
      </ReportParagraph>
      <div style={{ width: '100%' }}>
        <ReportFullWidthContainer height="150px" title="Threat Tickets">
          <Box
            sx={{
              flexDirection: 'row',
              display: 'flex',
              gap: 10,
              margin: '12px 0'
            }}
          >
            {bannerValues.map((item) => (
              <Box
                key={item.title}
                sx={{
                  flexDirection: 'column',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: 175
                }}
              >
                <Typography className={classes.threatTicketsBannerTitleText}>
                  {item.title}
                </Typography>
                <Typography className={classes.threatTicketsBannerNumberText}>
                  {item.total}
                </Typography>
                <div className={classes.threatTicketsBannerChangeText}>
                  <Chip
                    size="medium"
                    style={{
                      backgroundColor:
                        item.diff?.includes('-') ||
                        item.diff === '0.00' ||
                        item.diff === '+0.00' ||
                        item.diff === '-0.00'
                          ? '#Cad5cb'
                          : '#FECDD2',
                      color:
                        item.diff?.includes('-') ||
                        item.diff === '0.00' ||
                        item.diff === '+0.00' ||
                        item.diff === '-0.00'
                          ? '#1B5E20'
                          : '#8C0000'
                    }}
                    className={classes.chip}
                    label={
                      item.diff?.includes('-') ||
                      item.diff === '0.00' ||
                      item.diff === '+0.00' ||
                      item.diff === '-0.00'
                        ? `${item.diff}%`
                        : `+${item.diff}%`
                    }
                    variant="twoTone"
                    color="success"
                    icon={
                      <FontAwesomeIcon
                        icon={
                          item.diff.includes('-') ||
                          item.diff === '0.00' ||
                          item.diff === '+0.00' ||
                          item.diff === '-0.00'
                            ? ArrowDownIcon
                            : ArrowUpIcon
                        }
                      />
                    }
                  />
                  last month
                </div>
              </Box>
            ))}
          </Box>
        </ReportFullWidthContainer>
      </div>
      <Box
        sx={{
          flexDirection: 'row',
          display: 'flex',
          gap: 2,
          '& .MuiGrid-root': {
            justifyContent: 'space-around !important',
            '--Grid-columnSpacing': '0 !important',
            marginRight: '12px'
          }
        }}
      >
        <ReportsTemplateBoxContainer
          subChildren={REPORT_PARAGRAPHS.avgDetectionTimeDescription}
        >
          <ReportsTemplateCompareWidget
            title="Avg. Detection Time"
            value={
              <Box style={{ width: '210px', paddingLeft: '5%' }}>
                <AvgAckTimeWidget
                  showAllPriorities={false}
                  allPrioritiesAckTimes={mttdMetrics as TicketsAckTimesDTO[]}
                />
              </Box>
            }
          />
        </ReportsTemplateBoxContainer>
        <ReportsTemplateBoxContainer
          subChildren={REPORT_PARAGRAPHS.avgContainmentTimeDescription}
        >
          <ReportsTemplateCompareWidget
            title="Avg. Containment Time"
            value={
              <Box style={{ width: '210px', paddingLeft: '5%' }}>
                <AvgAckTimeWidget
                  showAllPriorities={false}
                  allPrioritiesAckTimes={mttcMetrics as TicketsAckTimesDTO[]}
                />
              </Box>
            }
          />
        </ReportsTemplateBoxContainer>
        <ReportsTemplateBoxContainer
          subChildren={REPORT_PARAGRAPHS.avgEscalationTimeDescription}
        >
          <ReportsTemplateCompareWidget
            title="Avg. Escalation Time"
            value={
              <Box style={{ width: '210px', paddingLeft: '5%' }}>
                <AvgAckTimeWidget
                  showAllPriorities={false}
                  allPrioritiesAckTimes={mttnMetrics as TicketsAckTimesDTO[]}
                />
              </Box>
            }
          />
        </ReportsTemplateBoxContainer>
      </Box>
      <Box sx={{ flexDirection: 'row', display: 'flex', gap: 2 }}>
        <Box sx={{ width: '49%' }}>
          <div
            data-testid="new-threat-ticket-by-severity"
            className={classes.execDonutChartContainer}
          >
            <Typography className={classes.reportsPreviewSubTitle}>
              NEW THREAT TICKET BY SEVERITY
            </Typography>
            <DonutChart
              data={newTicketsSeverityData.map((item) => ({
                name: item.name,
                count: Array.isArray(item.data) ? item.data[0] || 0 : 0,
                color: item.color
              }))}
            />
          </div>
          <ReportParagraph fontSize="12px">
            {REPORT_PARAGRAPHS.newThreatTicketDescription}
          </ReportParagraph>
        </Box>
        <div style={{ width: '2%' }}>
          <Box
            sx={{
              backgroundColor: '#D1E2D5',
              width: 2,
              height: '100%'
            }}
          />
        </div>
        <Box sx={{ width: '49%' }}>
          <div
            data-testid="top-new-threat-ticket-categories"
            style={{ height: 200 }}
          >
            <Typography className={classes.reportsPreviewSubTitle}>
              Top NEW Threat Ticket Categories
            </Typography>
            <HorizontalBarChart
              height={165}
              data={newTicketsCategoryData}
              series={REPORTS_EXEC_TICKETS_HORIZONTAL_BAR_SERIES}
            />
          </div>
          <ReportParagraph fontSize="12px">
            {REPORT_PARAGRAPHS.newThreatTicketCategoryDescription}
          </ReportParagraph>
        </Box>
      </Box>
    </div>
  );
};

export default ExecutiveReportPage2;
